import { Box, CircularProgress, Tab, Tabs } from '@mui/material';
import React from 'react'
import { useSelector, useDispatch } from 'react-redux';
import ExpandCollapseIbData from './OutboundRecordProperties/ExpandCollapseIbData';
import Tree from 'rc-tree';
import HideMappingButton from '../Mapping/HideMappingButton';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import { useEffect } from 'react';
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { treeActions } from '../../redux/slices/treeSlice';
import { getKeysToExpandForMappedNodes, findNodeByKey } from '../../utils/autoMapping';
import { eachDeep } from "deepdash-es/standalone";
import { scrollToMappedNode, scrollToSelectedItem } from "../../utils/utilFunctions";
import { getSelectedNodes } from '../RightContextMenu/ContextActions/CopyAndPaste';

const MappingPropertiesContainer = ({ setMiddleSelectedNode, currTab, setCurrTab }) => {
  const dispatch = useDispatch();
  const [treeData, setTreeData] = useState({
    header: [],
    body: [],
    trailer: [],
    dataUpdated: false
  });
  const inboundDependencyTree = useSelector(
    (state) =>
      state.treeData.outbound.leftTree.inboundDependencyTree
  );
  
  // Get mapping states for auto-expansion
  const mappingStates = useSelector(state => state.treeData.mappingStates);
  const outboundTree = useSelector(state => state.treeData.outbound);
  
  // Get left tree current tab from Redux
  const leftTreeCurrentTab = useSelector(state => state.treeData.outbound.leftTree.currentTab);
  
  const [selectedKeys, setSelectedKeys] = useState({});
  const { t } = useTranslation();
  
  // Helper function to find node by key in tree data
  const getNodeByKey = (_data, key, callback) => {
    let exitLoop = false;
    const loopNode = (data) => {
      data.forEach((item, index, arr) => {
        if (exitLoop) return false;
        if (!item) return;
        if (item.key === key) {
          exitLoop = true;
          return callback(item, index, arr);
        }
        if (item.children) loopNode(item.children);
      });
    };
    return loopNode(_data);
  };
  


  const iconRender = (node) => {
    return <span className={"tree-icon " + node.ICON_DISPLAY}></span>;
  };

  const switcherIcon = (obj) => {
    if (obj.isLeaf) return;
    if (obj.expanded) return <ArrowDropDownIcon />;
    else return <ArrowRightIcon sx={{ color: "#666666" }} />;
  };

  const titleRender = (node) => {
    return (
      <span data-key={`${node.key}`}>
        {node.title}
      </span>
    );
  };
    // Helper function to get parent keys for a given node key
  const getNodeParentsKeys = (key) => {
    if (!key) return [];
    const parentNodes = [];
    const keyArray = key.split("-");
    let tempKey = keyArray[0];
    for (let i = 1; i < keyArray.length; i++) {
      tempKey += `-${keyArray[i]}`;
      parentNodes.push(tempKey);
    }
    return parentNodes;
  };
  
  // Helper function to get all child keys of a node
  const getChildKeys = (nodeKey, treeData) => {
    const childKeys = [];
    const findChildren = (nodes) => {
      nodes.forEach(node => {
        if (node.key === nodeKey && node.children) {
          node.children.forEach(child => {
            childKeys.push(child.key);
            if (child.children) {
              findChildren(child.children);
            }
          });
        } else if (node.children) {
          findChildren(node.children);
        }
      });
    };
    findChildren(treeData);
    return childKeys;
  };

  // Helper function to filter out child nodes if parent is not expanded
  const filterCollapsedChildren = (selectedKeys, expandedKeys, treeData) => {
    const filteredKeys = [];
    const getNodeParentByKey = (key) => {
      if (!key) return "";
      const keyArray = key.split("-");
      keyArray.pop();
      return keyArray.join("-");
    };
    
    selectedKeys.forEach(key => {
      // Check if this key has any parent that is selected but not expanded
      const parentKey = getNodeParentByKey(key);
      let shouldInclude = true;
        if (selectedKeys.includes(parentKey) && !expandedKeys.includes(parentKey)) {
          // Parent is selected but not expanded, so exclude this child
          shouldInclude = false;
        }
      if (shouldInclude) {
        filteredKeys.push(key);
      }
    });
    
    return filteredKeys;
  };

  // Helper function to handle Shift+click range selection
  const getShiftSelectedKeys = (treeData, selectedKeys, clickedKey, expandedKeys, tab) => {
    if (selectedKeys.length === 0) {
      return [clickedKey];
    }
    
    // Get the last selected key as the anchor for the range
    const anchorKey = selectedKeys[selectedKeys.length - 1];
    
    // Get all node keys in order
    const allKeys = [];
    eachDeep(treeData, (node, key, parent, context) => {
      allKeys.push(node.key);
    });
    
    // Find the indices of the anchor and clicked keys in the allKeys array
    const anchorIndex = allKeys.indexOf(anchorKey);
    const clickedIndex = allKeys.indexOf(clickedKey);
    
    if (anchorIndex === -1 || clickedIndex === -1) {
      return selectedKeys;
    }
    
    // Get the range between anchor and clicked (using simple min/max for indices)
    const startIndex = Math.min(anchorIndex, clickedIndex);
    const endIndex = Math.max(anchorIndex, clickedIndex);
    const rangeKeys = allKeys.slice(startIndex, endIndex + 1);
    
    // Combine with existing selection
    const newSelection = [...selectedKeys];
    rangeKeys.forEach(key => {
      if (!newSelection.includes(key)) {
        newSelection.push(key);
      }
    });
    
    // Filter out child nodes if their parent is not expanded
    const currentExpandedKeys = expandedKeys[tab] || [];
    return filterCollapsedChildren(newSelection, currentExpandedKeys, treeData);
  };

  const selectHandler = (keys, info, treeData, tab) => {

    
    // Convert tab name to index for Redux
    const tabMapping = { 'header': 0, 'body': 1, 'trailer': 2 };
    const tabIndex = tabMapping[tab];
    
    // Always keep at least one node selected in source tree during mapping mode
    const isMappingMode = mappingStates.isMappingMode;
    const isLinkMode = mappingStates.isLinkMode;
    const currentSelectedKeys = selectedKeys[tab] || [];
    
    // Handle keyboard modifiers for proper multi-selection (same as OutboundTemplateTree)
    let kb = {
      shift: info.nativeEvent ? info.nativeEvent.shiftKey : false,
      ctrl: info.nativeEvent ? info.nativeEvent.ctrlKey : false,
      metaKey: info.nativeEvent ? info.nativeEvent.metaKey : false,
    };
    
    let finalSelectedKeys = [];
    
    if (!kb.ctrl && !kb.shift && !kb.metaKey) {
      // Simple click - single selection
      finalSelectedKeys = [info.node.key];
    } else if ((kb.ctrl || kb.metaKey) && !kb.shift) {
      // Ctrl/Cmd+click - add/remove from selection
      const clickedKey = info.node.key;
      if (currentSelectedKeys.includes(clickedKey)) {
        // Remove from selection if already selected
        finalSelectedKeys = currentSelectedKeys.filter(key => key !== clickedKey);
      } else {
        // Add to selection if not already selected
        finalSelectedKeys = [...currentSelectedKeys, clickedKey];
      }
    } else if (kb.shift) {
      // Shift+click - range selection
      const sourceTreeData = inboundDependencyTree?.[tab] || [];
      finalSelectedKeys = getShiftSelectedKeys(sourceTreeData, currentSelectedKeys, info.node.key, expandedKeys, tab);
    } else {
      finalSelectedKeys = keys;
    }
    
    if (isMappingMode) {
      // In link mode, only allow single node selection for drawing mapping lines
      if (isLinkMode) {
        // Override multi-selection behavior in link mode - always single selection
        finalSelectedKeys = [info.node.key];
        
        // If clicking on already selected node and link mode is on, keep it selected (don't unselect)
        if (finalSelectedKeys.length === 0 && currentSelectedKeys.length > 0 && !kb.ctrl && !kb.shift && !kb.metaKey) {
          // Prevent unselection - keep the current selection (only for simple clicks, not multi-selection)
          return;
        }
      }
      
      // Always allow selection (even if link mode is off for viewing purposes)
      if (finalSelectedKeys.length > 0) {
        setSelectedKeys((prev) => {
          return { ...prev, [tab]: finalSelectedKeys };
        });
        
        // For multiple selections when NOT in link mode, collect all selected nodes
        // In link mode, only single node for mapping line drawing
        const selectedNodes = finalSelectedKeys.map(key => {
          const sourceTreeData = inboundDependencyTree?.[tab] || [];
          return findNodeByKey(sourceTreeData, key);
        }).filter(node => node);
        
              // Dispatch selected nodes to Redux for automatic mapping
      dispatch(treeActions.setMappingSelectedSourceNodes({
        tab: tabIndex,
        selectedNodes: selectedNodes
      }));
      

      
      // Auto-switch right tree tab if there's a cross-tab mapping for this source node
      // Skip cross-tab logic if this is a double-click to avoid conflicts
      if (info.nativeEvent && info.nativeEvent.detail > 1) {
        return;
      }
      
      // Skip cross-tab logic if this was triggered by a right tree selection
      // Check if there's a cross-tab source node key in session storage
      const crossTabSourceNodeKey = sessionStorage.getItem('crossTabSourceNodeKey');
      if (crossTabSourceNodeKey) {
        return;
      }
      

      
      const allMappings = [...(mappingStates.mappings || []), ...(mappingStates.automaticMappings || [])];
      const mappingsForNode = allMappings.filter(mapping => 
        mapping.sourceKey === info.node.key || mapping.sourceKey === String(info.node.key)
      );

      
      if (mappingsForNode.length > 0) {
        // Prioritize body tab first, then header, then trailer
        const tabPriority = [1, 0, 2]; // body, header, trailer
        const tabNames = ['header', 'body', 'trailer'];
        
        let targetTabIndex = null;
        
        // Find the highest priority tab that has a mapping for this source node
        for (const priorityTabIndex of tabPriority) {
          const priorityTabName = tabNames[priorityTabIndex];
          const mappingInPriorityTab = mappingsForNode.find(mapping => 
            mapping.targetTab === priorityTabIndex
          );
          
          if (mappingInPriorityTab) {
            targetTabIndex = priorityTabIndex;
            break;
          }
        }
        
        // If no mapping found in priority tabs, fall back to the first mapping
        if (targetTabIndex === null && mappingsForNode.length > 0) {
          targetTabIndex = mappingsForNode[0].targetTab;
        }
        

        
        if (targetTabIndex !== undefined && targetTabIndex !== null && tabNames[targetTabIndex]) {

          
          // Get current right tree tab from Redux
          const currentRightTreeTab = outboundTree?.rightTree?.currentTab;
          const currentRightTreeTabIndex = tabNames.indexOf(currentRightTreeTab);
          
          // Only switch if the target tab is different from current right tree tab
          if (targetTabIndex !== currentRightTreeTabIndex) {
            // Switch right tree to the target tab using the correct Redux action
            dispatch(treeActions.setRightTreeCurrentTab({
              screen: "outbound",
              data: tabNames[targetTabIndex]
            }));
            
            // Set timestamp to indicate this was triggered by node selection
            sessionStorage.setItem('lastNodeSelectionTime', Date.now().toString());
          } else {
          }
        } else {
        }
      } else {
      }
      
    } else if (!isLinkMode) {
        // Allow deselection when link mode is off (read-only mode)
        setSelectedKeys((prev) => {
          return { ...prev, [tab]: finalSelectedKeys };
        });
        
        if (finalSelectedKeys.length === 0) {
          dispatch(treeActions.setMappingSelectedSourceNodes({
            tab: tabIndex,
            selectedNodes: []
          }));
        }
      }
    } else {
      // Normal behavior when not in mapping mode
      setSelectedKeys((prev) => {
        return { ...prev, [tab]: finalSelectedKeys };
      });
      
      // Dispatch selected node to Redux for automatic mapping (but only if in mapping mode)
      if (finalSelectedKeys.length > 0 && info.node && isMappingMode) {
        const selectedNodes = finalSelectedKeys.map(key => {
          const sourceTreeData = inboundDependencyTree?.[tab] || [];
          return findNodeByKey(sourceTreeData, key);
        }).filter(node => node);
        
        dispatch(treeActions.setMappingSelectedSourceNodes({
          tab: tabIndex,
          selectedNodes: selectedNodes
        }));
        
        // Auto-switch right tree tab if there's a cross-tab mapping for this source node

        
        const allMappings = [...(mappingStates.mappings || []), ...(mappingStates.automaticMappings || [])];

        
        const mappingsForNode = allMappings.filter(mapping => 
          mapping.sourceKey === info.node.key || mapping.sourceKey === String(info.node.key)
        );

        
        if (mappingsForNode.length > 0) {
          // Prioritize body tab first, then header, then trailer
          const tabPriority = [1, 0, 2]; // body, header, trailer
          const tabNames = ['header', 'body', 'trailer'];
          
          let targetTabIndex = null;
          
          // Find the highest priority tab that has a mapping for this source node
          for (const priorityTabIndex of tabPriority) {
            const priorityTabName = tabNames[priorityTabIndex];
            const mappingInPriorityTab = mappingsForNode.find(mapping => 
              mapping.targetTab === priorityTabIndex
            );
            
            if (mappingInPriorityTab) {
              targetTabIndex = priorityTabIndex;
              break;
            }
          }
          
          // If no mapping found in priority tabs, fall back to the first mapping
          if (targetTabIndex === null && mappingsForNode.length > 0) {
            targetTabIndex = mappingsForNode[0].targetTab;
          }
          

          
          if (targetTabIndex !== undefined && targetTabIndex !== null && tabNames[targetTabIndex]) {

            
            // Get current right tree tab from Redux
            const currentRightTreeTab = outboundTree?.rightTree?.currentTab;
            const currentRightTreeTabIndex = tabNames.indexOf(currentRightTreeTab);
            
            // Only switch if the target tab is different from current right tree tab
            if (targetTabIndex !== currentRightTreeTabIndex) {
              // Switch right tree to the target tab
              dispatch(treeActions.setCurrentMappingTab(targetTabIndex));
            
            // Update right tree tab in Redux
            dispatch(treeActions.setRightTreeCurrentTab({
              screen: "outbound",
              data: tabNames[targetTabIndex]
            }));
            
            // Set timestamp for node selection (this will be used by the right tree panel)
            sessionStorage.setItem('lastNodeSelectionTime', Date.now().toString());
          } else {
          }
        } else {
        }
        } else {
        }
      }
    }
  };

  // Handle double-click to scroll to mapped nodes in target tree
  const onDoubleClick = (info, tab) => {
    if (!mappingStates.isMappingMode) {
      return;
    }
    
    // Check if info is a SyntheticEvent (direct event from Tree component)
    let clickedNodeKey;
    if (info && info.nativeEvent && info.target) {
      // This is a SyntheticEvent, extract node key from the target element
      // Try multiple approaches to find the data-key
      let targetElement = info.target.closest('[data-key]');
      if (!targetElement) {
        // Try looking for rc-tree-treenode
        targetElement = info.target.closest('.rc-tree-treenode');
        if (targetElement) {
          const keyElement = targetElement.querySelector('[data-key]');
          if (keyElement) {
            targetElement = keyElement;
          }
        }
      }
      if (!targetElement) {
        // Try looking in parent elements more broadly
        let current = info.target;
        while (current && current !== document.body) {
          if (current.hasAttribute && current.hasAttribute('data-key')) {
            targetElement = current;
            break;
          }
          if (current.querySelector) {
            const found = current.querySelector('[data-key]');
            if (found) {
              targetElement = found;
              break;
            }
          }
          current = current.parentElement;
        }
      }
      
      if (targetElement) {
        clickedNodeKey = targetElement.getAttribute('data-key');
      } else {
        return;
      }
    } else if (info && info.node && info.node.key) {
      // This is a proper node info object
      clickedNodeKey = info.node.key;
    } else {
      return;
    }
    
    if (!clickedNodeKey) {
      return;
    }
    
    const tabMapping = ['header', 'body', 'trailer'];
    const currentTabIndex = tabMapping.indexOf(tab);
    
    // Call the utility function to scroll to mapped nodes
    // Note: Double-click should not trigger cross-tab navigation to avoid conflicts
    scrollToMappedNode(
      clickedNodeKey, 
      mappingStates.mappings, 
      mappingStates.automaticMappings, 
      true, // isSourceTree = true (this is source tree)
      currentTabIndex
    );
  };

  // Handle drag start for external drag to target tree
  const onDragStart = (info, tab) => {
    // Only allow drag when mapping mode is enabled but link mode is disabled
    if (mappingStates.isMappingMode && !mappingStates.isLinkMode) {
      const draggedKeys = selectedKeys[tab] || [];
      const clickedNodeKey = info.node.key;
      
      // If the dragged node is not in the current selection, select it first
      if (!draggedKeys.includes(clickedNodeKey)) {
        const newSelectedKeys = [clickedNodeKey];
        setSelectedKeys((prev) => {
          return { ...prev, [tab]: newSelectedKeys };
        });
        
        // Update Redux with the selected node
        const selectedNode = findNodeByKey(inboundDependencyTree?.[tab] || [], clickedNodeKey);
        if (selectedNode) {
          const tabMapping = { 'header': 0, 'body': 1, 'trailer': 2 };
          const tabIndex = tabMapping[tab];
          dispatch(treeActions.setMappingSelectedSourceNodes({
            tab: tabIndex,
            selectedNodes: [selectedNode]
          }));
        }
        
        // Use the newly selected node for drag
        const nodeKeys = [clickedNodeKey];
        const draggedNodes = [selectedNode].filter(node => node);
        
        const dragData = {
          nodes: draggedNodes,
          sourceTab: tab,
          keys: nodeKeys
        };
        
        dispatch(treeActions.setDragData(dragData));
        info.event.dataTransfer.setData('text/plain', JSON.stringify(dragData));
        info.event.dataTransfer.effectAllowed = 'copy';
      } else {
        // Node is already selected, use existing selection
        const nodeKeys = draggedKeys.length > 0 ? draggedKeys : [clickedNodeKey];
        
        // Collect node data for all dragged nodes
        const draggedNodes = getSelectedNodes(nodeKeys, inboundDependencyTree?.[tab] || [], false);

        // Save drag data to Redux instead of dataTransfer
        const dragData = {
          nodes: draggedNodes,
          sourceTab: tab,
          keys: nodeKeys
        };
        
        dispatch(treeActions.setDragData(dragData));
        
        // Still set dataTransfer for compatibility
        info.event.dataTransfer.setData('text/plain', JSON.stringify(dragData));
        info.event.dataTransfer.effectAllowed = 'copy';
      }
      
    } else {
      // Prevent drag if not in the right mode
      info.event.preventDefault();
    }
  };

  const onDragEnd = (info) => {
    // Clear drag data when drag ends (whether successful or not)
    // dispatch(treeActions.clearDragData());
  };

  const expandHandler = (keys, info, tab) => {
    setExpandedKeys((prev) => {
      return { ...prev, [tab]: keys };
    });
  };
  const handleExpandAndCollapse = (valuesObj) => {
    // Handle expand/collapse actions
    const { tab, keys } = valuesObj;
    
    if (keys && typeof keys === 'object') {
      // For expand - keys is an object with nodeId -> key mapping
      const keyArray = Object.values(keys);
      setExpandedKeys((prev) => {
        return { ...prev, [tab]: keyArray };
      });
    } else if (Array.isArray(keys)) {
      // For collapse - keys is an empty array
      setExpandedKeys((prev) => {
        return { ...prev, [tab]: keys };
      });
    }
  };
  const [expandedKeys, setExpandedKeys] = useState({});
  
  // Listen for expansion requests from cross-tab navigation (right tree to left tree)
  useEffect(() => {

    
    const sourceNodeKey = sessionStorage.getItem('crossTabSourceNodeKey');
    
    
    if (sourceNodeKey && mappingStates.isMappingMode) {
    
      
      // Get parent keys to expand
      const parentKeys = getNodeParentsKeys(sourceNodeKey);
  
      
      // Expand parent nodes
      if (parentKeys.length > 0) {
        const currentTabName = currTabMapping[currTab];
      
        
        setExpandedKeys(prev => {
          const currentExpanded = prev[currentTabName] || [];
          const newExpanded = [...new Set([...currentExpanded, ...parentKeys])];
          
          
          const newState = {
            ...prev,
            [currentTabName]: newExpanded
          };
         
          return newState;
        });
      }
      
      // Select the source node

      setSelectedKeys(prev => {
        const newState = { ...prev, [currTabMapping[currTab]]: [sourceNodeKey] };

        return newState;
      });
      
      // Get the actual node data and update Redux
      const sourceTreeData = inboundDependencyTree?.[currTabMapping[currTab]] || [];

      
      let sourceNodeData = null;
      getNodeByKey(sourceTreeData, sourceNodeKey, (item) => {
        sourceNodeData = item;
      });
      
      
      if (sourceNodeData) {
        dispatch(treeActions.setMappingSelectedSourceNodes({
          tab: currTabMapping[currTab],
          selectedNodes: [sourceNodeData]
        }));
      } else {
      }
      
      // Scroll to the node after a short delay to ensure expansion is complete
      setTimeout(() => {
        scrollToMappedNode(
          sourceNodeKey, 
          mappingStates.mappings || [], 
          mappingStates.automaticMappings || [], 
          true, // isSourceTree = true (this is source tree)
          currTab
        );
        
        // Clear the sessionStorage items (but don't clear lastRightTreeSelectionTime - let the right tree manage it)
        sessionStorage.removeItem('crossTabSourceNodeKey');
        sessionStorage.removeItem('lastNodeSelectionTime');
        // sessionStorage.removeItem('lastRightTreeSelectionTime'); // Don't clear this - let right tree manage it
      }, 200);
      
    } else {
    }
  }, [currTab]); // Removed expandedKeys from dependency array to prevent infinite loop
  
  const [tabEnable, setTabEnable] = useState({ body: true, header: true, trailer: true });
  const [isLoading, setIsLoading] = useState(false);
  const headerContainerRef = useRef(null);
  const bodyContainerRef = useRef(null);
  const trailerContainerRef = useRef(null);
  const currTabMapping = {
    0: "header",
    1: "body",
    2: "trailer"
  };

  useEffect(() => {
    setTreeData({ ...(inboundDependencyTree || {}) });
  }, [inboundDependencyTree])
  
  useEffect(() => {
    dispatch(treeActions.setCurrentMappingTab(currTab));
  }, [currTab, dispatch]);

  // Clear selected source nodes when switching tabs
  useEffect(() => {
    if (!mappingStates.isMappingMode) return;
    
    // Don't clear selections if this is a cross-tab navigation
    const crossTabSourceNodeKey = sessionStorage.getItem('crossTabSourceNodeKey');
    if (crossTabSourceNodeKey) {
      return;
    }
    
    // Clear all mapping selected source nodes when tab changes
    [0, 1, 2].forEach(tabIndex => {
      dispatch(treeActions.setMappingSelectedSourceNodes({
        tab: tabIndex,
        selectedNodes: []
      }));
    });
  }, [currTab, dispatch, mappingStates.isMappingMode]);

  // Sync local selectedKeys with Redux selectedSourceNodes state
  useEffect(() => {
    if (!mappingStates.isMappingMode) return;
    
    const newSelectedKeys = {};
    Object.keys(currTabMapping).forEach(tabIndex => {
      const tabName = currTabMapping[tabIndex];
      const selectedNodes = mappingStates.selectedSourceNodes[tabName] || [];
      if (selectedNodes.length > 0) {
        newSelectedKeys[tabName] = selectedNodes.map(node => node.key).filter(key => key);
      } else {
        newSelectedKeys[tabName] = [];
      }
    });
    
    setSelectedKeys(prev => {
      // Only update if there are actual changes to prevent unnecessary re-renders
      const hasChanges = Object.keys(newSelectedKeys).some(tab => {
        const prevKeys = prev[tab] || [];
        const newKeys = newSelectedKeys[tab] || [];
        if (prevKeys.length !== newKeys.length) return true;
        return prevKeys.some((key, index) => key !== newKeys[index]);
      });
      
      return hasChanges ? newSelectedKeys : prev;
    });
  }, [
    mappingStates.isMappingMode,
    mappingStates.selectedSourceNodes.header?.length,
    mappingStates.selectedSourceNodes.body?.length,
    mappingStates.selectedSourceNodes.trailer?.length,
    mappingStates.selectedSourceNodes.header,
    mappingStates.selectedSourceNodes.body,
    mappingStates.selectedSourceNodes.trailer
  ]);

  // Auto-expand left tree to reveal mapped nodes when target node is selected
  useEffect(() => {
    if (!mappingStates.isMappingMode) return;
    
    const currentTabName = currTabMapping[currTab];
    const selectedTargetNode = outboundTree?.rightTree?.tree?.[currentTabName]?.selectedNode;
    const selectedTargetNodes = mappingStates.selectedTargetNodes?.[currentTabName] || [];
    
    // Check both single selected target node and multiple selected target nodes
    const targetKeys = [];
    if (selectedTargetNode && selectedTargetNode.key) {
      targetKeys.push(selectedTargetNode.key);
    }
    selectedTargetNodes.forEach(node => {
      if (node && node.key && !targetKeys.includes(node.key)) {
        targetKeys.push(node.key);
      }
    });
    
    if (targetKeys.length > 0) {
      const combinedMappings = [...(mappingStates.mappings || []), ...(mappingStates.automaticMappings || [])];
      const sourceTreeData = inboundDependencyTree?.[currentTabName] || [];
      
      const allKeysToExpand = [];
      targetKeys.forEach(targetKey => {
        const keysToExpand = getKeysToExpandForMappedNodes(
          combinedMappings,
          targetKey,
          sourceTreeData,
          'target-to-source'
        );
        allKeysToExpand.push(...keysToExpand);
      });
      
      if (allKeysToExpand.length > 0) {
        setExpandedKeys(prev => {
          const currentExpanded = prev[currentTabName] || [];
          const uniqueKeysToExpand = [...new Set(allKeysToExpand)];
          const hasNewKeys = uniqueKeysToExpand.some(key => !currentExpanded.includes(key));
          
          if (hasNewKeys) {
            const newState = {
              ...prev,
              [currentTabName]: [...new Set([...currentExpanded, ...uniqueKeysToExpand])]
            };
            return newState;
          }
          return prev;
        });
      }
    }
  }, [
    mappingStates.isMappingMode, 
    outboundTree?.rightTree?.tree?.[currTabMapping[currTab]]?.selectedNode?.key,
    JSON.stringify(mappingStates.selectedTargetNodes[currTabMapping[currTab]]?.map(n => n?.key).sort() || []),
    mappingStates.mappings.length,
    mappingStates.automaticMappings.length,
    currTab
  ]);

  // Auto-expand left tree to reveal mapped nodes when source nodes are selected
  useEffect(() => {
    if (!mappingStates.isMappingMode) return;
    
    // Get all selected source nodes from all tabs
    const allSelectedSourceKeys = [];
    Object.values(mappingStates.selectedSourceNodes).forEach(nodeArray => {
      if (Array.isArray(nodeArray)) {
        nodeArray.forEach(node => {
          if (node && node.key) {
            allSelectedSourceKeys.push(node.key);
          }
        });
      }
    });
    
    if (allSelectedSourceKeys.length > 0) {
      const combinedMappings = [...(mappingStates.mappings || []), ...(mappingStates.automaticMappings || [])];
      
      // For each tab, expand nodes that are mapped to selected source nodes
      ['header', 'body', 'trailer'].forEach(tabName => {
        const sourceTreeData = inboundDependencyTree?.[tabName] || [];
        
        const allKeysToExpand = [];
        allSelectedSourceKeys.forEach(sourceKey => {
          const keysToExpand = getKeysToExpandForMappedNodes(
            combinedMappings,
            sourceKey,
            sourceTreeData,
            'source-to-target'
          );
          allKeysToExpand.push(...keysToExpand);
        });
        
        if (allKeysToExpand.length > 0) {
          setExpandedKeys(prev => {
            const currentExpanded = prev[tabName] || [];
            const uniqueKeysToExpand = [...new Set(allKeysToExpand)];
            const hasNewKeys = uniqueKeysToExpand.some(key => !currentExpanded.includes(key));
            
            if (hasNewKeys) {
              const newState = {
                ...prev,
                [tabName]: [...new Set([...currentExpanded, ...uniqueKeysToExpand])]
              };
              return newState;
            }
            return prev;
          });
        }
      });
    }
  }, [
    mappingStates.isMappingMode,
    JSON.stringify(mappingStates.selectedSourceNodes.header?.map(n => n?.key).sort() || []),
    JSON.stringify(mappingStates.selectedSourceNodes.body?.map(n => n?.key).sort() || []),
    JSON.stringify(mappingStates.selectedSourceNodes.trailer?.map(n => n?.key).sort() || []),
    mappingStates.mappings.length,
    mappingStates.automaticMappings.length,
    inboundDependencyTree
  ]);

  // Handle custom events for cross-tab navigation from target tree double-clicks
  useEffect(() => {
    const handleScrollToMappedNode = (event) => {
      const { type, tab, sourceKey } = event.detail;
      
      if (type === 'switchSourceTab') {
        const tabMapping = ['header', 'body', 'trailer'];
        const sourceTabName = tabMapping[tab];
        
        // Switch to the source tab
        setCurrTab(tab);
        
        // Scroll to the source node after tab switch
        setTimeout(() => {
          const sourceContainerName = `mappingTree${sourceTabName.charAt(0).toUpperCase() + sourceTabName.slice(1)}`;
          const sourceContainer = document.getElementById(sourceContainerName);
          if (sourceContainer) {
            const sourceElement = sourceContainer.querySelector(`[data-key="${sourceKey}"]`);
            if (sourceElement) {
              // Use scrollToSelectedItem utility for consistent scrolling
              scrollToSelectedItem([sourceKey], { current: sourceContainer });
              
              // Add highlight effect
              sourceElement.style.backgroundColor = '#e6f7ff';
              sourceElement.style.transition = 'background-color 0.3s ease';
              setTimeout(() => {
                sourceElement.style.backgroundColor = '';
              }, 1500);
            }
          }
        }, 200);
      }
    };
    
    window.addEventListener('scrollToMappedNode', handleScrollToMappedNode);
    
    return () => {
      window.removeEventListener('scrollToMappedNode', handleScrollToMappedNode);
    };
  }, [setCurrTab]);


  return (
    <div>
      <Box
        style={{
          height: "95vh !important",
        }}
      >
        <Box className="flex-column" style={{
          // height: "97vh",
        }}>
          <Box sx={{ zIndex: "9000", borderBottom: 1, borderColor: "divider", display: "flex", justifyContent: "space-between", height: "100%" }}>
            <Tabs
              value={currTab}
              onChange={(e, value) => setCurrTab(value)}
              style={{ display: 'flex', gap: '1px' }}
            >
              <Tab style={{ width: '5%' }} label={t("HEADER")} />
              <Tab style={{ width: '5%' }} label={t("BODY")} />
              <Tab style={{ width: '5%' }} label={t("TRAILER")} />
            </Tabs>
            <Box style={{ display: 'flex', gap: '0px' }} >
              <HideMappingButton side="source" />
              <ExpandCollapseIbData
                data={treeData[currTabMapping[currTab]] || []}
                setExpandActions={handleExpandAndCollapse}
                tab={currTabMapping[currTab]}
              />
            </Box>
          </Box>
          <Box className={"height-100-tab"} style={{ height: "95vh !important" }} id="mappingTreeContainer">
            {/* Render all tabs but only show the active one - this ensures all containers are always available for cross-tab mapping lines */}
            
            {/* header */}
            <div
              ref={headerContainerRef}
              className="overflow-auto"
              id="mappingTreeHeader"
              style={{ 
                height: "97vh",
                display: currTab === 0 ? 'block' : 'none' 
              }}
            >
              {isLoading ? (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflowY: "auto",
                  }}
                >
                  <CircularProgress />
                </div>
              ) : (

                tabEnable?.header ? <Tree
                  treeData={treeData?.header || []}
                  selectedKeys={selectedKeys?.header || []}
                  expandedKeys={expandedKeys?.header || []}
                  multiple={mappingStates.isMappingMode && !mappingStates.isLinkMode ? true : false}
                  draggable={mappingStates.isMappingMode && !mappingStates.isLinkMode}
                  onSelect={(keys, info) => {
                    selectHandler(keys, info, treeData?.header || [], "header");
                  }}
                  onDoubleClick={(info) => {
                    onDoubleClick(info, "header");
                  }}
                  onExpand={(keys, info) => {
                    expandHandler(keys, info, "header");
                  }}
                  onDragStart={(info) => {
                    onDragStart(info, "header");
                  }}
                  onDragEnd={onDragEnd}
                  onRightClick={() => { }}
                  icon={iconRender}
                  switcherIcon={switcherIcon}
                  titleRender={titleRender}
                  selectable
                  virtual={true}
                /> : <div className="panel-no-data">{t("DISABLE_TREE_MAP")}</div>
              )}
            </div>

            {/* body */}
            <div
              ref={bodyContainerRef}
              className="overflow-auto"
              id="mappingTreeBody"
              style={{ 
                height: "90vh",
                display: currTab === 1 ? 'block' : 'none' 
              }}
            >
              {isLoading ? (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflowY: "auto",
                  }}
                >
                  <CircularProgress />
                </div>
              ) : (
                tabEnable?.body ? <Tree
                  treeData={treeData?.body || []}
                  selectedKeys={selectedKeys?.body || []}
                  expandedKeys={expandedKeys?.body || []}
                  multiple={mappingStates.isMappingMode && !mappingStates.isLinkMode ? true : false}
                  draggable={mappingStates.isMappingMode && !mappingStates.isLinkMode}
                  onSelect={(keys, info) => {
                    selectHandler(keys, info, treeData?.body || [], "body");
                  }}
                  onDoubleClick={(info) => {
                    onDoubleClick(info, "body");
                  }}
                  onExpand={(keys, info) => {
                    expandHandler(keys, info, "body");
                  }}
                  onDragStart={(info) => {
                    onDragStart(info, "body");
                  }}
                  onDragEnd={onDragEnd}
                  onRightClick={() => { }}
                  icon={iconRender}
                  showIcon
                  selectable
                  switcherIcon={switcherIcon}
                  titleRender={titleRender}
                  virtual={true}
                /> : <div className="panel-no-data">{t("DISABLE_TREE_MAP")}</div>
              )}
            </div>

            {/* trailer */}
            <div
              ref={trailerContainerRef}
              className="overflow-auto"
              id="mappingTreeTrailer"
              style={{ 
                height: "90vh",
                display: currTab === 2 ? 'block' : 'none' 
              }}
            >
              {isLoading ? (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflowY: "auto",
                  }}
                >
                  <CircularProgress />
                </div>
              ) : (
                tabEnable?.trailer ? <Tree
                  treeData={treeData?.trailer || []}
                  selectedKeys={selectedKeys?.trailer || []}
                  expandedKeys={expandedKeys?.trailer || []}
                  multiple={mappingStates.isMappingMode && !mappingStates.isLinkMode ? true : false}
                  draggable={mappingStates.isMappingMode && !mappingStates.isLinkMode}
                  onSelect={(keys, info) => {
                    selectHandler(keys, info, treeData?.trailer || [], "trailer");
                  }}
                  onDoubleClick={(info) => {
                    onDoubleClick(info, "trailer");
                  }}
                  onExpand={(keys, info) => {
                    expandHandler(keys, info, "trailer");
                  }}
                  onDragStart={(info) => {
                    onDragStart(info, "trailer");
                  }}
                  onDragEnd={onDragEnd}
                  onRightClick={() => { }}
                  icon={iconRender}
                  switcherIcon={switcherIcon}
                  titleRender={titleRender}
                  selectable
                  virtual={true}
                /> : <div className="panel-no-data">{t("DISABLE_TREE_MAP")}</div>
              )}
            </div>
          </Box>
        </Box>
      </Box>
    </div>
  )
}

export default MappingPropertiesContainer
